import { getApiBaseUrl } from '../utils/apiBaseUrl';

// Chat Session Service for managing user chat sessions
class ChatSessionService {
  // Get the latest chat session for the current user
  static async getLatestSession() {
    try {
      // Get token from localStorage
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${getApiBaseUrl()}/api/chat-sessions/latest`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        // Don't throw error for 401 (unauthorized) - user just needs to login
        if (response.status === 401) {
          return { success: false, session: null, error: 'Unauthorized - please login' };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting latest session:', error);
      return { success: false, session: null, error: error.message };
    }
  }

  // Get all chat sessions for the current user
  static async getAllUserSessions() {
    try {
      // Get token from localStorage
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${getApiBaseUrl()}/api/chat-sessions/user`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          return { success: false, sessions: [], error: 'Unauthorized - please login' };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting user sessions:', error);
      return { success: false, sessions: [], error: error.message };
    }
  }

  // Create a new chat session
  static async createSession(businessType = null, language = 'en') {
    try {
      // Get token from localStorage
      const token = localStorage.getItem('authToken');
      console.log('Debug - Token from localStorage:', token ? 'Token exists' : 'No token');
      console.log('Debug - Token length:', token ? token.length : 0);
      
      const requestBody = { businessType, language };
      console.log('Debug - Request body:', requestBody);
      
      const response = await fetch(`${getApiBaseUrl()}/api/chat-sessions/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });
      
      console.log('Debug - Response status:', response.status);
      console.log('Debug - Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        // Don't throw error for 401 (unauthorized) - user just needs to login
        if (response.status === 401) {
          return { success: false, session: null, error: 'Unauthorized - please login' };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating session:', error);
      return { success: false, session: null, error: error.message };
    }
  }

  // Update a chat session with new messages
  static async updateSession(sessionId, messages, businessType = null) {
    try {
      // Get token from localStorage
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${getApiBaseUrl()}/api/chat-sessions/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ sessionId, messages, businessType }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          return { success: false, error: 'Unauthorized - please login' };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating session:', error);
      return { success: false, error: error.message };
    }
  }

  // Delete a chat session
  static async deleteSession(sessionId) {
    try {
      // Get token from localStorage
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${getApiBaseUrl()}/api/chat-sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          return { success: false, error: 'Unauthorized - please login' };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting session:', error);
      return { success: false, error: error.message };
    }
  }

  // Auto-save session (debounced to avoid too many API calls)
  static debounceSave = (() => {
    let timeoutId;
    return (sessionId, messages, businessType) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        this.updateSession(sessionId, messages, businessType);
      }, 1000); // Save after 1 second of inactivity
    };
  })();

  // Load session on app start
  static async loadSessionOnStart() {
    try {
      const result = await this.getLatestSession();
      
      if (result.success && result.session) {
        // Check if session is not too old (within 3 days)
        const sessionDate = new Date(result.session.updatedAt);
        const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000);
        
        if (sessionDate > threeDaysAgo) {
          return {
            success: true,
            session: result.session,
            messages: result.session.messages || [],
            businessType: result.session.businessType,
            language: result.session.language
          };
        } else {
          // Session is too old, delete it
          await this.deleteSession(result.session.sessionId);
          return { success: true, session: null, messages: [], businessType: null };
        }
      }
      
      return { success: true, session: null, messages: [], businessType: null };
    } catch (error) {
      console.error('Error loading session on start:', error);
      return { success: false, session: null, messages: [], businessType: null };
    }
  }

  // Start a new conversation (clear current session and create new one)
  static async startNewConversation(businessType = null, language = 'en') {
    try {
      const result = await this.createSession(businessType, language);
      
      if (result.success) {
        return {
          success: true,
          sessionId: result.session.sessionId,
          messages: [],
          businessType: businessType,
          language: language
        };
      } else {
        return result;
      }
    } catch (error) {
      console.error('Error starting new conversation:', error);
      return { success: false, error: error.message };
    }
  }

  // Get conversation statistics
  async getStats() {
    try {
      const response = await fetch(`${this.baseURL}/stats`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.stats;
    } catch (error) {
      console.error('Error fetching stats:', error);
      throw error;
    }
  }
}

export default ChatSessionService; 